<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell
    :title="$t('views.selectedGoat.modules.generalInfo')"
    :icon="Info"
    @click="clickable ? goToGeneralInfoView() : null">
    <div v-if="goat" class="grid grid-cols-2 gap-4 text-sm">
      <div>
        <strong>{{ $t('views.goats.table.name') }}:</strong><br />
        {{ goat.name }}
      </div>

      <div>
        <strong>{{ $t('views.goats.table.gender') }}:</strong><br />
        {{ translatedGender }}
      </div>

      <div>
        <strong>{{ $t('views.goats.table.breed') }}:</strong><br />
        {{ translatedBreed }}
      </div>

      <div>
        <strong>{{ $t('views.goats.table.birthDate') }}:</strong><br />
        {{ formatDate(goat.dateOfBirth) }}
      </div>

      <div>
        <strong>{{ $t('views.goats.table.birthPlace') }}:</strong><br />
        {{ goat.placeOfBirth }}
      </div>

      <div>
        <strong>{{ $t('views.goats.table.group') }}:</strong><br />
        {{ groupName }}
      </div>
    </div>
  </ModuleShell>
</template>

<script setup>
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
  import { Info } from 'lucide-vue-next'
  import { formatDate } from '@/utils/format/dateformatting.js'

  const router = useRouter()
  const { t } = useI18n()

  const props = defineProps({
    goat: { type: Object, required: true },
    groupName: { type: String, default: '—' },
    clickable: { type: Boolean, default: true },
  })

  // Translate gender value
  const translatedGender = computed(() => {
    if (!props.goat?.gender) return '—'
    return props.goat.gender === 'Male' 
      ? t('views.goats.goatForm.male')
      : t('views.goats.goatForm.female')
  })

  
  // Translate breed value
  const translatedBreed = computed(() => { 
    const breed = props.goat?.breed 
    if (!breed) return '—' 
    switch (breed) { 
      case 'Burenziege': 
        return t('views.goats.goatForm.breed.boerGoat') 
      case 'Thüringer Waldziege': 
        return t('views.goats.goatForm.breed.thuringianGoat') 
      default: 
        return '—' } 
    })

    console.log('Breed received:', props.goat?.breed)


  //Redirect to GeneralInfo View
  function goToGeneralInfoView() {
    router.push({ name: 'GeneralInfo', params: { id: props.goat.id } })
  }
</script>
